// Component to allow event sounds to be heard even when not on the Connect View
import {Alert} from '@mui/material';
import {Howl} from 'howler';
import {uniq} from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';

import API from '../../api/axios';
import {apiBaseUrl} from '../../api/urls';
import {useConfiguration, useConfigurations} from '../../hooks/configuration';
import {useAppSelector} from '../../hooks/redux';
import {useRefreshInterval} from '../../hooks/refreshInterval';
import {EventSummaryResponse} from '../../interfaces/Event';
import reduxActions from '../../redux/actions';
import reduxSelectors from '../../redux/selectors';
import {getMsFromS} from '../../utils/datetime';
import {CONNECT_EVENT_SOUNDS, HAZARD_AI_EVENT_SOUNDS} from '../../utils/events';
import {SOUNDS} from '../../utils/sounds';
import {isPresent} from '../../utils/type-guards';

export const EventSummarySounds = () => {
  const [fetchedEventSummaryErrors, setFetchedEventSummaryErrors] = useState<
    string[]
  >([]);

  const reduxDispatch = useDispatch();
  const company = useAppSelector(({assets}) => assets.company);
  const configurations = useConfigurations();
  const lastEventIds = useAppSelector(({app}) => app.lastEventIds);

  let dateParam = new Date();
  const offset = dateParam.getTimezoneOffset();
  dateParam = new Date(dateParam.getTime() - offset * 60 * 1000);
  const dateString = dateParam.toISOString().substring(0, 10);

  const fetchEventSummary = useCallback(async () => {
    setFetchedEventSummaryErrors([]);
    try {
      const params = {
        date_start: dateString,
        date_end: dateString,
      };
      const resp = await API.get<EventSummaryResponse>(
        `${apiBaseUrl}/event/summary`,
        {params}
      );
      if (soundFiles.length === 0) {
        setSoundFiles(getSoundFilesFromEventSummary(resp.data));
      }
    } catch (error: any) {
      // todo handle error
    }
  }, [configurations, lastEventIds]);

  const eventSummaryRefresh = useConfiguration(
    'commtrac',
    'commtrac_sound_interval'
  );
  const eventSummaryRefreshInterval = eventSummaryRefresh?.value
    ? getMsFromS(+eventSummaryRefresh?.value)
    : null;
  useRefreshInterval(fetchEventSummary, eventSummaryRefreshInterval);

  useEffect(() => {
    fetchEventSummary();
  }, []);

  const newEventSoundConfiguration = useConfiguration(
    'commtrac',
    'sound_new_event'
  );

  const getSoundFilesFromEventSummary = (
    eventSummary: EventSummaryResponse
  ) => {
    const soundNames: (string | number)[] = [];

    const connectSounds = eventSummary.connect.sound
      .map((i) => {
        const configKey = CONNECT_EVENT_SOUNDS[i.type];
        return configKey ? configurations[configKey]?.value : null;
      })
      .filter(isPresent);
    soundNames.push(...connectSounds);

    if (company?.ams_enabled) {
      const amsSounds = eventSummary.ams.sound
        .map((i) => {
          const configKey = CONNECT_EVENT_SOUNDS[i.type];
          return configKey ? configurations[configKey]?.value : null;
        })
        .filter(isPresent);
      soundNames.push(...amsSounds);
    }

    if (company?.hazard_ai_enabled) {
      const hazardAiSounds = eventSummary.hazard_ai.sound
        .map((i) => {
          const configKey = HAZARD_AI_EVENT_SOUNDS[i.type];
          return configKey ? configurations[configKey]?.value : null;
        })
        .filter(isPresent);
      soundNames.push(...hazardAiSounds);
    }

    if (company?.belt_enabled) {
      const beltSounds = eventSummary.belt.sound
        .map((i) => {
          const configKey = CONNECT_EVENT_SOUNDS[i.type];
          return configKey ? configurations[configKey]?.value : null;
        })
        .filter(isPresent);
      soundNames.push(...beltSounds);
    }

    if (company?.shaft_enabled) {
      const shaftSounds = eventSummary.shaft_clearance.sound
        .map((i) => {
          const configKey = CONNECT_EVENT_SOUNDS[i.type];
          return configKey ? configurations[configKey]?.value : null;
        })
        .filter(isPresent);
      soundNames.push(...shaftSounds);
    }

    let hasNewEvents = false;
    const newLastEventIds = {...lastEventIds};

    if (
      eventSummary.connect.last_id &&
      eventSummary.connect.last_id > (lastEventIds.connect ?? 0)
    ) {
      hasNewEvents = true;
      newLastEventIds.connect = eventSummary.connect.last_id;
    }

    if (
      company?.ams_enabled &&
      eventSummary.ams.last_id &&
      eventSummary.ams.last_id > (lastEventIds.ams ?? 0)
    ) {
      hasNewEvents = true;
      newLastEventIds.ams = eventSummary.ams.last_id;
    }

    if (
      company?.hazard_ai_enabled &&
      eventSummary.hazard_ai.last_id &&
      eventSummary.hazard_ai.last_id > (lastEventIds.hazard_ai ?? 0)
    ) {
      hasNewEvents = true;
      newLastEventIds.hazard_ai = eventSummary.hazard_ai.last_id;
    }

    if (
      company?.belt_enabled &&
      eventSummary.belt.last_id &&
      eventSummary.belt.last_id > (lastEventIds.belt ?? 0)
    ) {
      hasNewEvents = true;
      newLastEventIds.belt = eventSummary.belt.last_id;
    }

    if (
      company?.shaft_enabled &&
      eventSummary.shaft_clearance.last_id &&
      eventSummary.shaft_clearance.last_id > (lastEventIds.shaft_clearance ?? 0)
    ) {
      hasNewEvents = true;
      newLastEventIds.shaft_clearance = eventSummary.shaft_clearance.last_id;
    }

    if (hasNewEvents) {
      soundNames.unshift(newEventSoundConfiguration?.value ?? '');
      reduxActions.app.setApp(reduxDispatch, {
        lastEventIds: newLastEventIds,
      });
    }

    return uniq(soundNames.map((i) => SOUNDS[i]).filter(isPresent));
  };

  const muteSounds = useAppSelector(reduxSelectors.app.getSoundMuteMode);
  const [soundFiles, setSoundFiles] = useState<any[]>([]);

  const sound = useMemo(() => {
    const src = soundFiles[0];
    return src ? new Howl({src}) : null;
  }, [soundFiles]);

  useEffect(() => {
    if (sound) {
      if (muteSounds) {
        return;
      }

      if (sound.state() === 'loaded') {
        sound.play();
      } else {
        sound.once('load', () => sound.play());
      }
      sound.once('end', () => {
        setSoundFiles(soundFiles.slice(1));
      });

      return () => {
        sound.unload();
      };
    }
  }, [sound]);

  return (
    <>
      {fetchedEventSummaryErrors.map((error, idx) => (
        <Alert
          key={`error-es-${idx}`}
          severity="error"
          onClose={fetchEventSummary}
        >
          {error}
        </Alert>
      ))}
    </>
  );
};
